// Zero-contention retry wrappers for Potidaea claim paths.
//
// Every claim() targets the shared reservoir UTXO of the target MBA. If two
// wallets fetch the same reservoir tip and broadcast concurrently, only one
// wins the UTXO race — the loser gets `txn-mempool-conflict (18)` or
// `bad-txns-inputs-missingorspent`. Same phenomenon measured on chipnet in
// Parameters/ASM_Native/ASM-no-shared-state-contention.md (1/6 under naïve
// parallel broadcast).
//
// The fix is tier-4 from Parameters/ASM_Native/ASM-single-asset-throughput.md
// §2d: on conflict, re-run the whole build → sign → broadcast pipeline (which
// fetches a fresh reservoir tip, mempool-inclusive) and rebroadcast. Bounded
// attempts + linear backoff. Fully trustless — no sequencer.
//
// This is the dApp-side counterpart to shells/app/src/asm-core/retry.ts.
// Difference: the dApp calls the build+broadcast in one shot (there is no
// separate plan step surfaced to the UI), so retry re-runs the whole
// claimVia* function each attempt — which re-fetches the reservoir.

import type { WcClient } from './wc';
import type { ElectrumClient } from './electrum';
import type { Signer } from './signer-store';
import {
  claimViaWc, claimViaLocalSigner, claimViaLocalSignerShare,
  type ClaimTxResult,
} from './orbital';

const DEFAULT_RETRY_MAX = 5;
const DEFAULT_RETRY_BACKOFF_MS = 200;

/** Match the strings BCHN / cashscript / electrum surface when the target
 *  reservoir UTXO was spent by a concurrent tx between plan and broadcast. */
export function isReservoirRace(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err);
  return /txn-mempool-conflict|bad-txns-inputs-missingorspent|missing[- ]?inputs?|already[- ]?spent/i.test(msg);
}

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

export interface RetryOpts {
  /** Max total attempts including the first. Default 5. */
  readonly maxAttempts?: number;
  /** Linear backoff base in ms — attempt n waits `n × backoffMs` after failing. Default 200. */
  readonly backoffMs?: number;
  /** UI hook fired BEFORE every attempt (including 1). Lets the pending sheet
   *  show "resolving contention — attempt n / max" when n > 1. */
  readonly onAttempt?: (attempt: number, max: number) => void;
}

export interface RetryResult<R> {
  readonly result: R;
  /** How many attempts it took. `> 1` = contention was hit and resolved. */
  readonly attempts: number;
}

/** Generic retry loop shared by all three claim variants. */
async function withRetry<R>(
  fn: () => Promise<R>,
  opts: RetryOpts,
): Promise<RetryResult<R>> {
  const max = opts.maxAttempts ?? DEFAULT_RETRY_MAX;
  const backoff = opts.backoffMs ?? DEFAULT_RETRY_BACKOFF_MS;
  let lastErr: unknown;
  for (let attempt = 1; attempt <= max; attempt++) {
    opts.onAttempt?.(attempt, max);
    try {
      const result = await fn();
      return { result, attempts: attempt };
    } catch (e) {
      lastErr = e;
      if (!isReservoirRace(e) || attempt >= max) throw e;
      await sleep(backoff * attempt);
    }
  }
  throw lastErr;
}

/** Artemision / DeviantOpenMintFT claim with client-side retry on reservoir race. */
export function claimViaLocalSignerWithRetry(
  signer: Signer,
  args: { ticker: string; amountUnits: bigint },
  opts: RetryOpts = {},
): Promise<RetryResult<ClaimTxResult>> {
  return withRetry(() => claimViaLocalSigner(signer, args), opts);
}

/** DeviantShareMBA claim with client-side retry on reservoir race. */
export function claimViaLocalSignerShareWithRetry(
  signer: Signer,
  args: { ticker: string; amountUnits: bigint },
  opts: RetryOpts = {},
): Promise<RetryResult<ClaimTxResult>> {
  return withRetry(() => claimViaLocalSignerShare(signer, args), opts);
}

/** WalletConnect-signed claim with client-side retry on reservoir race.
 *  Every attempt re-builds the tx with a fresh reservoir tip and asks the
 *  paired wallet to re-sign. The user sees one WC prompt per retry — bounded
 *  by maxAttempts so a stuck wallet can't infinite-loop the popup. */
export function claimViaWcWithRetry(
  wc: WcClient,
  electrum: ElectrumClient,
  args: { ticker: string; amountUnits: bigint; payerCashaddr: string; recipientTokenaddr?: string },
  opts: RetryOpts = {},
): Promise<RetryResult<ClaimTxResult>> {
  return withRetry(() => claimViaWc(wc, electrum, args), opts);
}
